import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';

import { ScanVaultService } from 'api/services/services';

const PATCH_TREND_PATH = '/v1/scan-vault/vulnerability/patch-trend';

export interface PatchTrendPoint {
  day: string;
  resource_count: number;
}

export interface PatchTrend {
  from: string;
  to: string;
  items: PatchTrendPoint[];
}

export interface PatchTrendParams {
  environmentId?: string;
  accountId?: string | string[];
  /** YYYY-MM-DD, inclusive. */
  startDate?: string;
  /** YYYY-MM-DD, inclusive. */
  endDate?: string;
}

// The generated client has no patch-trend operation until @ariksa/scan-vault
// is republished, so the request uses the same Axios instance, base path and
// bearer token the generated client uses.
type PatchTrendClient = {
  axios: AxiosInstance;
  basePath: string;
  configuration?: {
    accessToken?: string | (() => string | Promise<string>);
  };
};

export function serializePatchTrendQuery(input: PatchTrendParams) {
  const query = new URLSearchParams();
  if (input.environmentId) query.append('environment_id', input.environmentId);
  const accounts = input.accountId
    ? Array.isArray(input.accountId)
      ? input.accountId
      : [input.accountId]
    : [];
  accounts.forEach(account => query.append('account_uuid', account));
  if (input.startDate) query.set('from', `${input.startDate}T00:00:00Z`);
  if (input.endDate) query.set('to', `${input.endDate}T00:00:00Z`);
  return query.toString();
}

export async function getVulnerabilityPatchTrend(
  input: PatchTrendParams,
): Promise<PatchTrend> {
  const client = (ScanVaultService.Vulnerability as unknown) as PatchTrendClient;
  const token = client.configuration?.accessToken;
  const bearer = typeof token === 'function' ? await token() : token;
  const query = serializePatchTrendQuery(input);
  const response = await client.axios.get(
    `${client.basePath}${PATCH_TREND_PATH}${query ? `?${query}` : ''}`,
    { headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined },
  );
  const data = response?.data;
  if (!data || typeof data !== 'object' || !Array.isArray((data as any).items))
    throw new Error('Vulnerability patch trend is temporarily unavailable');
  return data as PatchTrend;
}

/** Shape of the legacy reporting chart the widget renders: day -> count. */
export function mapPatchTrendToLegacy(trend: PatchTrend) {
  const series: Record<string, number> = {};
  for (const item of trend.items) {
    series[item.day.slice(0, 10)] = item.resource_count;
  }
  return { exploitable_entities_to_patch: series };
}

// ScanVault serves at most 92 days of patch trend.
export function clampPatchTrendStart(startDate?: string, endDate?: string) {
  if (!startDate || !endDate) return startDate;
  const earliest = dayjs(endDate).subtract(91, 'day');
  return dayjs(startDate).isBefore(earliest)
    ? earliest.format('YYYY-MM-DD')
    : startDate;
}
